import {
  Address,
  encodeFunctionData,
  formatEther,
  formatUnits,
  getAddress,
  isAddress,
  parseAbi,
  parseEther,
  parseUnits,
} from "viem";

export type TransferAssetType = "native" | "governance" | "erc20" | "erc721" | "erc1155";

export interface AssetTransferInput {
  assetType: TransferAssetType;
  treasuryAddress: Address;
  governanceTokenAddress: Address;
  governanceTokenSymbol: string;
  customAssetAddress: string;
  recipient: string;
  amount: string;
  tokenId: string;
}

export interface ReadClient {
  getBalance: (parameters: { address: Address }) => Promise<bigint>;
  readContract: (parameters: Record<string, unknown>) => Promise<unknown>;
}

export interface AssetTransferAction {
  target: Address;
  value: bigint;
  calldata: `0x${string}`;
  summary: string;
}

const ERC20_ABI = parseAbi([
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function balanceOf(address account) view returns (uint256)",
  "function transfer(address recipient, uint256 amount) returns (bool)",
]);

const ERC721_ABI = parseAbi([
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function safeTransferFrom(address from, address to, uint256 tokenId)",
]);

const ERC1155_ABI = parseAbi([
  "function balanceOf(address account, uint256 id) view returns (uint256)",
  "function safeTransferFrom(address from, address to, uint256 id, uint256 amount, bytes data)",
]);

const positiveInteger = (value: string, label: string) => {
  if (!/^\d+$/.test(value)) throw new Error(`${label} must be a whole number.`);
  const parsed = BigInt(value);
  if (parsed < 0n) throw new Error(`${label} cannot be negative.`);
  return parsed;
};

export async function buildAssetTransferAction(
  input: AssetTransferInput,
  client: ReadClient,
): Promise<AssetTransferAction> {
  if (!isAddress(input.recipient)) throw new Error("Enter a valid Polygon recipient address.");
  const recipient = getAddress(input.recipient);

  if (input.assetType === "native") {
    const value = parseEther(input.amount);
    if (value <= 0n) throw new Error("Enter a positive POL amount.");
    const balance = await client.getBalance({ address: input.treasuryAddress });
    if (value > balance) throw new Error(`Treasury balance is ${formatEther(balance)} POL.`);
    return {
      target: recipient,
      value,
      calldata: "0x",
      summary: `Transfer ${input.amount} POL from the DAO treasury to ${recipient}.`,
    };
  }

  const assetAddress = input.assetType === "governance"
    ? input.governanceTokenAddress
    : isAddress(input.customAssetAddress)
      ? getAddress(input.customAssetAddress)
      : undefined;
  if (!assetAddress) throw new Error("Enter a valid Polygon asset contract address.");

  if (input.assetType === "governance" || input.assetType === "erc20") {
    const [decimalsResult, symbolResult, balanceResult] = await Promise.all([
      client.readContract({ address: assetAddress, abi: ERC20_ABI, functionName: "decimals" }),
      client.readContract({ address: assetAddress, abi: ERC20_ABI, functionName: "symbol" }).catch(() => "ERC-20"),
      client.readContract({ address: assetAddress, abi: ERC20_ABI, functionName: "balanceOf", args: [input.treasuryAddress] }),
    ]);
    const decimals = Number(decimalsResult);
    const balance = balanceResult as bigint;
    const amount = parseUnits(input.amount, decimals);
    if (amount <= 0n) throw new Error("Enter a positive token amount.");
    if (amount > balance) throw new Error(`Treasury balance is ${formatUnits(balance, decimals)} ${String(symbolResult)}.`);
    const symbol = input.assetType === "governance" ? input.governanceTokenSymbol : String(symbolResult);
    return {
      target: assetAddress,
      value: 0n,
      calldata: encodeFunctionData({ abi: ERC20_ABI, functionName: "transfer", args: [recipient, amount] }),
      summary: `Transfer ${input.amount} ${symbol} from the DAO treasury to ${recipient}. Token: ${assetAddress}.`,
    };
  }

  const tokenId = positiveInteger(input.tokenId, "Token ID");
  if (input.assetType === "erc721") {
    const owner = await client.readContract({ address: assetAddress, abi: ERC721_ABI, functionName: "ownerOf", args: [tokenId] }) as Address;
    if (owner.toLowerCase() !== input.treasuryAddress.toLowerCase()) throw new Error("The DAO treasury does not own this ERC-721 token ID.");
    return {
      target: assetAddress,
      value: 0n,
      calldata: encodeFunctionData({
        abi: ERC721_ABI,
        functionName: "safeTransferFrom",
        args: [input.treasuryAddress, recipient, tokenId],
      }),
      summary: `Transfer ERC-721 token #${tokenId} from ${assetAddress} to ${recipient}.`,
    };
  }

  const amount = positiveInteger(input.amount, "Amount");
  if (amount <= 0n) throw new Error("Enter a positive ERC-1155 amount.");
  const balance = await client.readContract({
    address: assetAddress,
    abi: ERC1155_ABI,
    functionName: "balanceOf",
    args: [input.treasuryAddress, tokenId],
  }) as bigint;
  if (amount > balance) throw new Error(`Treasury balance for token #${tokenId} is ${balance}.`);
  return {
    target: assetAddress,
    value: 0n,
    calldata: encodeFunctionData({
      abi: ERC1155_ABI,
      functionName: "safeTransferFrom",
      args: [input.treasuryAddress, recipient, tokenId, amount, "0x"],
    }),
    summary: `Transfer ${amount} of ERC-1155 token #${tokenId} from ${assetAddress} to ${recipient}.`,
  };
}
